import { useMemo, useState } from "react";
import {
  useGetWarehousesQuery,
  useDeleteWarehouseMutation,
} from "../../../../redux/api";
import type { Warehouse } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { WarehouseFormModal } from "./warehouse";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 6;

const statusTone = (status?: string | null) =>
  (status ?? "").toLowerCase() === "active" ? "badge--success" : "badge--warning";

// Inventory → Warehouses (list page). Create/edit happen in a popup
// (WarehouseFormModal, from warehouse.tsx).
const Warehouses = () => {
  const {
    data: warehouses = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetWarehousesQuery();

  const [deleteWarehouse] = useDeleteWarehouseMutation();

  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = warehouses.find((w) => w.id === viewingId) ?? null;


  const handleDelete = async (warehouse: Warehouse) => {
    if (!window.confirm(`Delete "${warehouse.name}"?`)) return;
    try {
      await deleteWarehouse(warehouse.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete warehouse.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return warehouses;
    return warehouses.filter((w: Warehouse) =>
      [w.name, w.code, w.city, w.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [warehouses, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load warehouses"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Warehouses"
          onBack={() => setViewingId(null)}
          title={viewing.name}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Code", value: viewing.code },
              { label: "Type", value: viewing.type },
              { label: "Capacity", value: viewing.capacity },
              { label: "Contact person", value: viewing.contactPerson },
              { label: "Email", value: viewing.email },
              { label: "Phone", value: viewing.phone },
              { label: "Address line 1", value: viewing.addressLine1, full: true },
              { label: "Address line 2", value: viewing.addressLine2, full: true },
              { label: "City", value: viewing.city },
              { label: "State", value: viewing.state },
              { label: "Country", value: viewing.country },
              { label: "Postal code", value: viewing.postalCode },
            ]}
          />
        </EntityOverview>
        <WarehouseFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Warehouses</h2>
            <p className="panel__sub">
              Showing {visible.length} of {warehouses.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search warehouses…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New warehouse
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>City</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading warehouses…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {warehouses.length === 0
                      ? "No warehouses yet."
                      : "No warehouses match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((warehouse: Warehouse) => (
                  <tr key={warehouse.id}>
                    <td>
                      <span className="pill pill--neutral">{warehouse.code}</span>
                    </td>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(warehouse.id)}
                      >
                        {warehouse.name}
                      </button>
                    </td>
                    <td>{warehouse.type ?? "—"}</td>
                    <td>{warehouse.city ?? "—"}</td>
                    <td>
                      <span className={`badge ${statusTone(warehouse.status)}`}>
                        <span className="badge__dot" />
                        {warehouse.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View warehouse",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(warehouse.id),
                          },
                          {
                            label: "Edit warehouse",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(warehouse.id),
                          },
                          {
                            label: "Delete warehouse",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(warehouse),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <WarehouseFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Warehouses;
